/**
 * THE ONE Info affordance: the button an operator presses on a transmission row, and the two
 * panels it opens. Used by the dashboard / global run history AND by the batch report's item table.
 *
 * WHY THIS IS A COMPONENT AND NOT A SECOND COPY. The batch report needed the same button, and the
 * obvious way to get it -- paste the button and the panels into that page too -- is how the original
 * defect got in and how it would come back. This affordance carries a provider decision (below),
 * and a duplicated provider decision is one that gets fixed in one place and stays wrong in the
 * other. Two call sites, one implementation.
 *
 * THE PROVIDER IS RESOLVED SERVER-SIDE, AND THE CLICK PASSES NO PROVIDER AT ALL. This matters more
 * than it looks. The outbound handle column is named `efax_fax_id`, but it is provider-agnostic:
 * SFax stores its SendFaxQueueId there and SRFax its FaxDetailsID. Info used to be dispatched on
 * `Boolean(efax_fax_id)`, which routed EVERY SFax run to eFax metadata and printed an SFax queue id
 * under the heading "eFax fax_id" -- an id an operator would then quote to the wrong desk. So:
 *
 *   - the only argument is the row's own id;
 *   - `GET /api/transmissions/{id}/outbound-info` resolves the leg's provider (row column -> job
 *     column -> DEFAULT_PROVIDER) and reports which of the three it used via `provider_recorded`;
 *   - every label is taken from that answer (`outbound_handle_label`), never from a literal.
 *
 * A caller that wanted to "help" by passing a provider would reintroduce the bug, so there is no
 * parameter for one.
 *
 * WORKS FOR A BATCH ITEM AS WELL AS A RUN, which is what let the batch report reuse it: the
 * endpoint resolves BOTH tables (`resolve_by_id` tries `Run` then `BatchRun`). The eFax metadata
 * REFRESH does not -- `POST /api/runs/{id}/check-status` is a runs-only route and 404s on a batch
 * item id -- so that call is gated on the endpoint's own `kind`. A batch item gets the
 * outbound-info panel, which already holds the provider-resolved record; it does not get an error
 * popup, which is what an ungated refresh produced.
 */
import { useState } from 'react'
import { api, formatApiError, OutboundInfo } from '../api/client'
import { fmtDateTime } from '../utils/datetime'
import { canRefreshOutboundStatus, outboundHandleLabel } from '../utils/outboundHandle'
import { describeTimestamp } from './transmissionTiming'
import { theme } from '../theme'
import { statusColor, statusLabel } from '../utils/runStatus'
import { StructuredRecord } from './StructuredRecord'

/**
 * Friendly names for the SFax callback fields, with the wire name kept visible beneath each.
 *
 * Only the fields SFax's callback actually carries are named here; anything else it starts
 * sending still renders under its own key, so this map cannot hide a new field by omission.
 */
const SFAX_CALLBACK_LABELS: Record<string, string> = {
  faxid: 'Fax ID',
  fromfaxnumber: 'From number',
  tofaxnumber: 'To number',
  intofaxnumber: 'Arrival DID',
  faxdateiso: 'Provider completion time',
  faxpages: 'Pages, as the provider counted them',
  faxstatus: 'Provider status word',
  faxsuccess: 'Provider success flag',
  resultcode: 'Result code',
  errorcode: 'Error code',
  remoteid: 'Remote fax identifier',
}

export function OutboundInfoButton({
  transmissionId,
  onRefreshed,
  title,
}: {
  /** The row's OWN id -- a run id or a batch-item id. No provider is passed; see the module note. */
  transmissionId: string
  /** Called after an eFax status refresh has written new timestamps, so the caller can reload. */
  onRefreshed?: () => void
  title?: string
}) {
  const [checking, setChecking] = useState(false)
  const [metaPanel, setMetaPanel] = useState<Awaited<ReturnType<typeof api.checkRunStatus>> | null>(null)
  const [smtpPanel, setSmtpPanel] = useState<OutboundInfo | null>(null)

  // WHERE THIS ROW'S RECEPTION TIME CAME FROM, through the SHARED describer rather than a
  // second reading of the provenance registry. `describeTimestamp` is what run detail and the
  // measurements panel use, so the compact tag this panel shows ("provider", "our arrival
  // clock", "source not recorded") cannot contradict the sentence those pages show for the same
  // row — two surfaces disagreeing about a reading's authority destroys the point of recording
  // it. Computed only when there IS a reception time: a tag on an empty cell would describe the
  // source of nothing.
  const receivedProvenance = smtpPanel?.t_received
    ? describeTimestamp('t_received', {
        t_received: smtpPanel.t_received,
        t_received_source: smtpPanel.t_received_source,
        t_notified: smtpPanel.t_notified,
        inbound_provider: smtpPanel.inbound.provider,
        inbound_transport: smtpPanel.inbound.transport,
        timestamp_provenance: smtpPanel.timestamp_provenance,
      })
    : null

  const handleInfo = async () => {
    setChecking(true)
    try {
      const info = await api.getOutboundInfo(transmissionId)
      // The live refresh is a RUNS-ONLY route. `kind` is the endpoint's own answer about which
      // table the id came from, so a batch item takes the outbound-info panel instead of a 404.
      //
      // ONE PREDICATE, NOT A PER-PROVIDER CHAIN. `canRefreshOutboundStatus` answers whether a
      // live provider lookup exists for this leg — eFax's `/faxes/{id}/metadata`, SRFax's
      // `Get_FaxStatus` or SFax's `SendFaxStatus` — and the endpoint dispatches on the row's
      // provider server-side. Asking here which provider it is would put that decision back in
      // the client, which is the defect this component's module note exists to describe.
      if (
        info.kind !== 'batch_item' &&
        canRefreshOutboundStatus(info.outbound.provider, info.outbound.transport, info.outbound_handle)
      ) {
        const refreshed = await api.checkRunStatus(transmissionId)
        onRefreshed?.()
        setMetaPanel(refreshed)
        return
      }
      setSmtpPanel(info)
    } catch (err: any) {
      window.alert(formatApiError(err) || 'Status lookup failed')
    } finally {
      setChecking(false)
    }
  }

  return (
    <>
      <button
        onClick={handleInfo}
        disabled={checking}
        title={title || "Show this transmission's outbound record: the provider and transport that carried it, whatever was captured locally, and why anything is missing"}
        style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', background: theme.bgAlt, color: '#4dd0e1', border: '1px solid #2a5a60', borderRadius: '4px', cursor: 'pointer', marginRight: '0.4rem' }}
      >
        {checking ? '…' : 'Info'}
      </button>
      {smtpPanel && (
        <>
          <div onClick={() => setSmtpPanel(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 40 }} />
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(520px, 92vw)', background: theme.bgAlt, color: theme.text, boxShadow: '-2px 0 12px rgba(0,0,0,0.5)', zIndex: 41, overflowY: 'auto', padding: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              {/* Labelled from the RESOLVED provider and transport, never from a literal. An
                  unlabelled empty panel cannot be told apart from the wrong panel — and a
                  panel headed "SMTP Transmission Info" above an SFax API run WAS the wrong
                  panel, silently. */}
              <h3 style={{ margin: 0 }}>{smtpPanel.outbound.label} — Outbound Info</h3>
              <button onClick={() => setSmtpPanel(null)} style={{ border: 'none', background: 'transparent', color: theme.text, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.4rem 0.75rem', fontSize: '0.85rem' }}>
              <strong>{smtpPanel.kind === 'batch_item' ? 'Batch item status' : 'Run Status'}</strong><span><span style={{ color: statusColor(smtpPanel.status), fontWeight: 600 }}>{statusLabel(smtpPanel.status)}</span></span>
              <strong>Outbound leg</strong>
              <span>
                {smtpPanel.outbound.label}
                {!smtpPanel.outbound.provider_recorded && (
                  <span style={{ color: '#9aa0a6', fontSize: '0.75rem' }}>
                    {' '}· provider not recorded on this row; taken from the job's{' '}
                    current configuration
                  </span>
                )}
              </span>
              <strong>Inbound leg</strong><span>{smtpPanel.inbound.label}</span>
              <strong>Outbound Status</strong><span>{smtpPanel.outbound_status || '—'}</span>
              {/* The handle under the name ITS OWN provider gives it. This row used to read
                  "eFax fax_id" unconditionally, which put SFax's SendFaxQueueId under another
                  provider's name — an id an operator would then quote to the wrong desk. */}
              <strong>{smtpPanel.outbound_handle_label}</strong>
              <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', wordBreak: 'break-all' }}>
                {smtpPanel.outbound_handle || '—'}
              </span>
              <strong>Submitted</strong><span>{fmtDateTime(smtpPanel.t_submitted || undefined)}</span>
              <strong>Fax Sent</strong><span>{fmtDateTime(smtpPanel.t_fax_sent || undefined)}</span>
              {/* THE INBOUND LEG, WHICH THIS PANEL DID NOT SHOW AT ALL (W2-2A-1).
                  The live panel below has always shown `Received`, and it opens only for a
                  provider/transport pair with a live provider lookup — so the reception time
                  looked like an eFax-eAPI and SRFax-API feature while every SFax run and every
                  SMTP-sent run landed here and saw an outbound clock and nothing else, on rows
                  whose `t_received` was populated and correct. The engine measured both legs;
                  this surface published one.

                  WITH ITS SOURCE, through the SAME describer run detail uses, so a reading
                  taken from our own arrival clock cannot be read here as the provider's. */}
              <strong>Received</strong>
              <span>
                {smtpPanel.t_received ? fmtDateTime(smtpPanel.t_received) : '—'}
                {receivedProvenance && (
                  <span style={{ color: '#9aa0a6', fontSize: '0.75rem' }}>
                    {' '}· {receivedProvenance.sourceTag}
                  </span>
                )}
              </span>
              {/* NOT the same fact as `Received`, and shown only when it differs: `t_notified`
                  is OUR clock at the moment we learned of the arrival, so the gap between the
                  two is our own pickup lag — an IMAP poll interval, a queue wait — and folding
                  it into the leg would charge our latency to the provider. */}
              {smtpPanel.t_notified && smtpPanel.t_notified !== smtpPanel.t_received && (
                <>
                  <strong>We were notified</strong>
                  <span>
                    {fmtDateTime(smtpPanel.t_notified)}
                    <span style={{ color: '#9aa0a6', fontSize: '0.75rem' }}>
                      {' '}· our own clock, not part of the measured leg
                    </span>
                  </span>
                </>
              )}
            </div>

            {/* Every note the endpoint chose, each on its own. The important one is the third
                case: for an SMTP outbound leg with a non-IMAP inbound method, no mailbox is
                ever polled, so no Send Notification will EVER arrive — which is what this
                panel used to describe as "not yet". */}
            {smtpPanel.notes.length > 0 && (
              <div style={{ marginTop: '1rem', display: 'grid', gap: '0.5rem' }}>
                {smtpPanel.notes.map((n, i) => (
                  <p key={i} style={{ fontSize: '0.8rem', color: '#ffa726', margin: 0, background: theme.bgElevated, border: `1px solid ${theme.border}`, borderRadius: 6, padding: '0.6rem' }}>
                    {n}
                  </p>
                ))}
              </div>
            )}

            {/* The headline field. For a lost transmission this is the string to
                follow through the REMOTE mail server's logs, and it is the only such
                handle an SMTP run has — eFax exposes no metadata endpoint. */}
            <h4 style={{ margin: '1.25rem 0 0.4rem' }}>Remote Envelope Message ID</h4>
            {smtpPanel.smtp_message_id ? (
              <code style={{ display: 'block', background: theme.bgElevated, border: `1px solid ${theme.border}`, borderRadius: '6px', padding: '0.6rem', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                {smtpPanel.smtp_message_id}
              </code>
            ) : (
              <p style={{ fontSize: '0.8rem', color: '#9aa0a6', margin: 0 }}>
                Not recorded. The receiving server's reply used a format the parser did
                not recognise, or this run predates envelope-ID capture. The raw reply
                was logged verbatim at send time — search the app log for this run token.
              </p>
            )}

            <h4 style={{ margin: '1.25rem 0 0.4rem' }}>
              Send Notification Headers
              {smtpPanel.notification_received_at && (
                <span style={{ fontWeight: 400, fontSize: '0.75rem', color: '#9aa0a6' }}>
                  {' '}· received {fmtDateTime(smtpPanel.notification_received_at)}
                </span>
              )}
            </h4>
            {/* notification_received is the authority, NOT the emptiness of x_headers:
                an empty dict cannot distinguish "nothing arrived" from "arrived with
                no headers", and R3 requires telling the operator which it is. The reason
                itself is in the notes block above, so it is not repeated here. */}
            {!smtpPanel.notification_received ? (
              <p style={{ fontSize: '0.8rem', color: '#9aa0a6', margin: 0 }}>
                No Send Notification headers are stored for this transmission — see the note
                above for why.
              </p>
            ) : Object.keys(smtpPanel.x_headers).length === 0 ? (
              <p style={{ fontSize: '0.8rem', color: '#9aa0a6', margin: 0 }}>
                The notification carried no X-* headers.
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <tbody>
                  {Object.entries(smtpPanel.x_headers).map(([k, v]) => (
                    <tr key={k} style={{ borderTop: `1px solid ${theme.border}` }}>
                      <td style={{ padding: '0.3rem 0.5rem 0.3rem 0', fontFamily: 'monospace', color: '#82b1ff', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{k}</td>
                      <td style={{ padding: '0.3rem 0', fontFamily: 'monospace', wordBreak: 'break-word' }}>{String(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {smtpPanel.notification_received && Object.keys(smtpPanel.all_headers).length > 0 && (
              <>
                <h4 style={{ margin: '1.25rem 0 0.5rem' }}>All Notification Headers</h4>
                <StructuredRecord
                  record={smtpPanel.all_headers}
                  emptyText="The notification carried no headers."
                  rawLabel="Raw header block"
                  theme={theme}
                />
              </>
            )}

            {/* SFax's own parsed callback record, durably stored under sfax-pending/ and read
                by nothing until now — which is why SFax had no working Info view at all. */}
            {smtpPanel.sfax_callback_record && (
              <>
                <h4 style={{ margin: '1.25rem 0 0.5rem' }}>SFax Callback Record</h4>
                {smtpPanel.sfax_callback_record.available ? (
                  <>
                    <div style={{ fontSize: '0.72rem', color: '#9aa0a6', marginBottom: '0.35rem', wordBreak: 'break-all' }}>
                      {smtpPanel.sfax_callback_record.key}
                    </div>
                    <StructuredRecord
                      record={smtpPanel.sfax_callback_record.record}
                      labels={SFAX_CALLBACK_LABELS}
                      emptyText="The stored callback record is empty."
                      rawLabel="Raw callback record"
                      theme={theme}
                    />
                  </>
                ) : (
                  <p style={{ fontSize: '0.8rem', color: '#9aa0a6', margin: 0 }}>
                    {smtpPanel.sfax_callback_record.reason}
                  </p>
                )}
              </>
            )}

            {/* Every record the row holds, NAMED BY ITS COLUMN. Without the column name an
                operator cannot tell a Send Notification header from an inbound webhook header
                from an IMAP delivery header, and those answer different questions about
                different legs. A source with nothing in it is still listed, so "this row has
                no such record" cannot be misread as "this build has no such panel". */}
            <h4 style={{ margin: '1.25rem 0 0.5rem' }}>Recorded Sources</h4>
            <div style={{ display: 'grid', gap: '0.6rem' }}>
              {smtpPanel.sources.map(s => (
                <div key={s.source} style={{ background: theme.bgElevated, border: `1px solid ${theme.border}`, borderRadius: 6, padding: '0.6rem' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    {s.label}{' '}
                    {/* THREE STATES, and the middle one is the point. A source that cannot
                        apply to these legs is not an empty record: rendering it as "nothing
                        recorded" invites a reader to go looking for a delivery that was never
                        going to exist, or to read the blank as evidence something failed.
                        `applicable === null` means the transport is unknown, which is said
                        plainly rather than resolved in either direction. */}
                    <span
                      style={{
                        fontWeight: 400,
                        fontSize: '0.72rem',
                        color: s.present
                          ? '#4caf50'
                          : s.applicable === false
                            ? '#6d6d6d'
                            : '#9aa0a6',
                        fontStyle: s.applicable === false ? 'italic' : 'normal',
                      }}
                    >
                      {s.present
                        ? 'recorded'
                        : s.applicable === false
                          ? 'does not apply to this transmission'
                          : s.applicable === null
                            ? 'nothing recorded — this row does not say which transport it used'
                            : s.stub
                              ? 'not implemented'
                              : 'nothing recorded'}
                    </span>
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#82b1ff' }}>
                    {s.source} · {s.leg}
                  </div>
                  {s.reason && (
                    <p style={{ fontSize: '0.75rem', color: '#9aa0a6', margin: '0.4rem 0 0' }}>{s.reason}</p>
                  )}
                  {s.present && s.data && Object.keys(s.data).length > 0 && (
                    <StructuredRecord
                      record={s.data}
                      emptyText="This source recorded no fields."
                      rawLabel="Raw stored value"
                      theme={theme}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {metaPanel && (
        <>
          <div onClick={() => setMetaPanel(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 40 }} />
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(460px, 90vw)', background: theme.bgAlt, color: theme.text, boxShadow: '-2px 0 12px rgba(0,0,0,0.5)', zIndex: 41, overflowY: 'auto', padding: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              {/* NAMED FROM THE RESOLVED PROVIDER, never the literal "Fax Metadata" it used to
                  be. This panel is now opened for SRFax's `Get_FaxStatus` record as well as
                  eFax's metadata, and an unlabelled panel cannot be told apart from the wrong
                  one — which is the whole defect class the `efax_fax_id` label fix addressed. */}
              <h3 style={{ margin: 0 }}>
                {metaPanel.outbound_provider_label || outboundHandleLabel(metaPanel.outbound_provider)} — Transmission Record
              </h3>
              <button onClick={() => setMetaPanel(null)} style={{ border: 'none', background: 'transparent', color: theme.text, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.4rem 0.75rem', fontSize: '0.85rem' }}>
              <strong>Run Status</strong><span><span style={{ color: statusColor(metaPanel.status), fontWeight: 600 }}>{statusLabel(metaPanel.status)}</span></span>
              <strong>Outbound Status</strong><span>{metaPanel.outbound_status || '—'}</span>
              {/* Labelled from the provider the ENDPOINT resolved, not from a literal: the
                  column is the provider-agnostic outbound handle, and this panel is only ever
                  opened for eFax now — but a hardcoded label would go wrong again the moment
                  that changes, which is exactly how it went wrong the first time. */}
              <strong>{metaPanel.outbound_handle_label || outboundHandleLabel(metaPanel.outbound_provider)}</strong>
              <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', wordBreak: 'break-all' }}>{metaPanel.efax_fax_id || '—'}</span>
              <strong>Submitted</strong><span>{fmtDateTime(metaPanel.t_submitted)}</span>
              <strong>Fax Sent</strong><span>{fmtDateTime(metaPanel.t_fax_sent)}</span>
              <strong>Received</strong><span>{fmtDateTime(metaPanel.t_received)}</span>
            </div>
            {metaPanel.metadata_error && (
              <p style={{ color: '#ef5350', fontSize: '0.8rem', marginTop: '1rem' }}>Lookup note: {metaPanel.metadata_error}</p>
            )}
            {metaPanel.note && (
              <p style={{ color: '#ffa726', fontSize: '0.8rem', marginTop: '1rem' }}>{metaPanel.note}</p>
            )}
            {metaPanel.metadata && (
              <>
                {/* The provider's OWN words, verbatim. Headed by the provider that said them:
                    an SRFax `Get_FaxStatus` record under the heading "Raw eFax Metadata" is one
                    provider's record under another's name, which is what sends an operator to
                    the wrong support desk. */}
                <h4 style={{ margin: '1.25rem 0 0.5rem' }}>
                  {metaPanel.outbound_provider_label || 'Provider'} record
                </h4>
                <StructuredRecord
                  record={metaPanel.metadata}
                  emptyText="The provider returned no metadata for this fax."
                  rawLabel={`Raw ${metaPanel.outbound_provider_label || 'provider'} response`}
                  theme={theme}
                />
              </>
            )}
          </div>
        </>
      )}
    </>
  )
}
